/**
 * Our own rating scale.
 *
 * Separate from the public score a card shows (`Movie.rating`, TMDB's vote
 * average): a number produced by a different crowd says little about whether
 * *this* one enjoyed the film. So the app collects its own as well.
 */
export const RATING_MIN = 1;
export const RATING_MAX = 10;
export function isValidRating(score) {
    return (typeof score === 'number' &&
        Number.isInteger(score) &&
        score >= RATING_MIN &&
        score <= RATING_MAX);
}
/**
 * How many ratings a film needs before its average is taken at face value.
 *
 * Below this the score is pulled towards the middle of the scale. The naive
 * alternative — showing the raw mean from the first vote — puts a film that
 * one person rated 10 above one that fifty people rated 9, which is both
 * wrong and the first thing anybody notices.
 */
export const RATING_CONFIDENCE_THRESHOLD = 5;
/** The value a film with no ratings is pulled towards: the middle. */
export const RATING_PRIOR = (RATING_MIN + RATING_MAX) / 2;
/**
 * Summarises a film's ratings.
 *
 * The weighted score is a Bayesian average: the real ratings plus
 * `RATING_CONFIDENCE_THRESHOLD` imaginary ones sitting at the middle of the
 * scale. With few votes the middle dominates; with many it disappears. No
 * cutoff, no "not enough ratings" gap — a film simply earns its position as
 * people rate it.
 */
export function summarizeRatings(scores) {
    const count = scores.length;
    if (count === 0) {
        return { count: 0, average: null, weighted: null, confident: false };
    }
    const total = scores.reduce((sum, score) => sum + score, 0);
    const average = total / count;
    const weighted = (total + RATING_CONFIDENCE_THRESHOLD * RATING_PRIOR) / (count + RATING_CONFIDENCE_THRESHOLD);
    return {
        count,
        average,
        weighted,
        confident: count >= RATING_CONFIDENCE_THRESHOLD,
    };
}
/** One decimal is as much precision as this many opinions can support. */
export function formatRating(value) {
    return value.toFixed(1);
}
/**
 * Votes a public average needs before a card shows it or the minimum-rating
 * filter trusts it. TMDB's long tail is full of films rated by a handful of
 * people, and their 9.4s would otherwise top every "8+" deck.
 */
export const RATING_MIN_VOTES = 50;
